import { Link } from "react-router-dom";
import { ArrowRight, Users, FileText, CreditCard, BarChart3, Share2, Shield, Bot, Monitor, FolderKanban, PenTool, Building2 } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const modules = [
  { icon: Users, title: "CRM Clients", desc: "Gerez vos clients avec des fiches detaillees, historique d'interactions, segmentation et suivi commercial. Attribuez des plans tarifaires personnalises.", color: "#10B981" },
  { icon: FileText, title: "Gestion Devis", desc: "Creez des propositions commerciales professionnelles avec tarification dynamique. Transformez un devis en contrat en un clic.", color: "#8B5CF6" },
  { icon: PenTool, title: "Contrats & Signature", desc: "Generez des contrats PDF personnalises et faites-les signer electroniquement par vos clients. Workflow complet du devis au contrat.", color: "#06B6D4" },
  { icon: CreditCard, title: "Facturation & Paiements", desc: "Facturation automatisee avec integration Stripe. Paiements en ligne securises, relances automatiques et suivi des encaissements.", color: "#F59E0B" },
  { icon: FolderKanban, title: "Gestion Projets", desc: "Suivez vos projets de A a Z avec suivi d'avancement, jalons, fichiers associes et historique. Importation depuis GitHub.", color: "#EF4444" },
  { icon: BarChart3, title: "SEO & Analytics", desc: "Connectez Google Analytics 4 et Search Console. Suivez les positions, trafic, mots-cles et performances SEO de vos clients.", color: "#0099CC" },
  { icon: Shield, title: "Surveillance Concurrents", desc: "Analysez les positions de vos concurrents sur les moteurs de recherche. Identifiez les opportunites et ajustez votre strategie.", color: "#EC4899" },
  { icon: Bot, title: "Assistant IA", desc: "Intelligence artificielle integree pour generer du contenu, des series de publications et analyser vos donnees automatiquement.", color: "#A855F7" },
  { icon: Monitor, title: "Dashboard Performance", desc: "Tableaux de bord KPI personnalises pour admins et clients. Visualisez revenus, conversions, leads et activite en temps reel.", color: "#14B8A6" },
  { icon: Share2, title: "Reseaux Sociaux", desc: "Planifiez et publiez sur Facebook, Instagram, LinkedIn et TikTok. Calendrier editorial, series IA et statistiques d'engagement.", color: "#F97316" },
  { icon: Building2, title: "Multi-Agences SaaS", desc: "Architecture multi-tenant permettant de gerer plusieurs agences depuis une meme plateforme. Marque blanche personnalisable.", color: "#64748B" },
  { icon: CreditCard, title: "Commissions & Affilies", desc: "Systeme de commissions pour vos affilies avec suivi des performances, calcul automatique et historique des paiements.", color: "#10B981" },
];

export default function PlatformPage() {
  usePageTitle("La plateforme GLOTECH OS");
  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-12">
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">La plateforme digitale<br /><span className="text-[#0099CC]">tout-en-un</span></h1>
          <p className="text-lg text-[#94A3B8] leading-relaxed">GLOTECH OS reunit 12 modules essentiels dans une seule interface. Gerez vos clients, projets, finances et marketing depuis un tableau de bord unique.</p>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map(m => (
            <div key={m.title} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#0099CC]/30 transition-all group" data-testid={`module-${m.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110" style={{ backgroundColor: `${m.color}12` }}>
                <m.icon className="h-6 w-6" style={{ color: m.color }} />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{m.title}</h3>
              <p className="text-[#64748b] text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="bg-[#080C14] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16">Tunnel commercial automatise</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {["Lead capture", "Proposition", "Signature", "Contrat PDF", "Facture", "Paiement", "Projet"].map((step, i) => (
              <div key={step} className="flex items-center gap-4">
                <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl px-6 py-4 text-center">
                  <div className="text-[#0099CC] font-bold text-lg mb-1">{i + 1}</div>
                  <div className="text-white text-sm font-medium">{step}</div>
                </div>
                {i < 6 && <ArrowRight className="h-5 w-5 text-[#2A3441] hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">Decouvrez GLOTECH OS en action</h2>
        <p className="text-[#94A3B8] mb-8">Demandez une demonstration personnalisee pour votre secteur d'activite.</p>
        <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-8 py-4 rounded-xl font-semibold transition-all" data-testid="platform-cta">
          Demander une demo <ArrowRight className="h-5 w-5" />
        </Link>
      </section>
    </div>
  );
}
